// Global imports
import { useCallback, useEffect, useMemo, useState } from 'react'
import { trackPageInfo, toFixedNumber } from '~/utils'
import { useDebugLogger } from '~/hooks'
import { getBrendaMaxQtyFallback } from '~/utils/brendaStock'

// Types
import type {
  BrendaChargesExtra,
  BrendaChargesPickup,
  BrendaChargesResult,
} from '~/types/brendaCharges'
import type { LunarProductPreBasket } from '~/types'

// ---------------------------------------------------------------------------
// Extras display shape
// ---------------------------------------------------------------------------

/**
 * UI-state wrapper around a `BrendaChargesExtra`. Holds the selected
 * quantity + the resolved max-quantity for the user's current
 * fulfilment, plus a few convenience fields lifted from the parent
 * product's category so each extras card can be rendered standalone.
 */
export interface PurchaseModalExtra extends BrendaChargesExtra {
  quantity: number
  minQuantity: number
  maxQuantity: number
  /** Parent product's category — used for tracking + Lunar meta. */
  category: string
  /** Copied from the parent product's groupId so add-on lines carry it. */
  parentGroupId: string
  /** `name` alias for `ProductExtrasCard` (which wants both). */
  name: string
}

// ---------------------------------------------------------------------------
// Availability helpers — native `BrendaChargesExtra` shape
// ---------------------------------------------------------------------------

/**
 * Resolves the usable max quantity for an add-on / cross-sell based on
 * the user's current fulfilment.
 *
 * First the **universal gate**: every linked add-on carries a resolved
 * date (its earliest Brenda-confirmed availability, via `mapBrendaExtra`
 * → `resolveBrendaDeliveryStock`). If that date lands later than the
 * parent's resolved delivery date, `mapBrendaExtra` has already set
 * `extra.delivery.available = false`. That flag is treated as the
 * authoritative "can this extra land in the parent's window at all?"
 * signal and gates **both** fulfilment routes — pickup included —
 * because the extra's stock genuinely isn't there yet regardless of
 * which route the user takes.
 *
 * Then per-fulfilment:
 *
 *   - **Delivery:** returns `extra.delivery.maxQuantity` (already
 *     stock-capped to the product-type fallback by `mapBrendaExtra`).
 *   - **Pickup:** reads the chosen branch's `linked` list. The raw
 *     branch stock isn't fallback-capped upstream (it comes straight
 *     off `linkedProductAvailability`), so we cap it here — hire
 *     lines must never be addable past 10, buy past 1000. Missing /
 *     zero branch entries return 0.
 */
const resolveExtraMax = (
  extra: BrendaChargesExtra,
  pickup: BrendaChargesPickup,
  fulfilment: 'delivery' | 'pickup' | null
): number => {
  // Universal parent-window gate. `delivery.available = false` here
  // means the extra's Brenda-resolved date is after the parent's, so
  // it can't be fulfilled in time via any route.
  if (!extra.delivery.available) return 0

  if (fulfilment === 'pickup') {
    if (!pickup.available) return 0
    const match = pickup.linked.find(l => l.stockCode === extra.stockCode)
    const branchStock = match?.maxQuantity ?? 0
    const fallback = getBrendaMaxQtyFallback(extra.productType)
    return Math.min(branchStock, fallback)
  }
  return extra.delivery.maxQuantity
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

interface UsePurchaseModalParams {
  open: boolean
  charges: BrendaChargesResult | null
  category?: string
  fulfilment: string | null
  /** The user's selected quantity for the parent line — mandatory add-ons scale to this. */
  parentQuantity: number

  // Tracking + UI labels
  productName: string
  /** `'bulk'` → Hire, anything else → Buy. */
  productType: string
  loggedIn: boolean

  /** Called when the modal closes. */
  onClose: () => void
}

/**
 * usePurchaseModal
 *
 * Owns the extras-modal state: currentStage, add-ons / cross-sells
 * lists, derived basket payload, quantity rules, grosss total and
 * tracking. Consumes the new `BrendaChargesResult` shape directly —
 * reads add-ons / cross-sells from `charges.product` and resolves
 * per-item availability via `charges.pickup.linked` or the extra's
 * own `delivery` summary.
 */
const usePurchaseModal = ({
  open,
  charges,
  category,
  fulfilment,
  parentQuantity,
  productName,
  productType,
  loggedIn,
  onClose,
}: UsePurchaseModalParams) => {
  const debugRef = useDebugLogger('DEBUG_PDP_FLOW')

  // ----------------------------
  // Local UI state
  // ----------------------------
  const [currentStage, setCurrentStage] = useState<number>(1)
  const [addOns, setAddOns] = useState<PurchaseModalExtra[]>([])
  const [crossSells, setCrossSells] = useState<PurchaseModalExtra[]>([])

  // ----------------------------
  // Build addOns + crossSells from the charges result when the modal
  // opens. Deliberately gated on `open` so mid-session re-fetches don't
  // blow away the user's in-progress selections.
  // ----------------------------
  useEffect(() => {
    if (!open) return
    if (!charges) return

    const product = charges.product
    const pickup = charges.pickup

    // Buy products are always delivery — fall back if fulfilment state
    // hasn't been seeded by the time the modal opens.
    const resolvedFulfilment =
      (fulfilment as 'delivery' | 'pickup' | null) ??
      (productType !== 'bulk' ? 'delivery' : null)

    debugRef.current('[usePurchaseModal] build extras from charges', {
      groupId: product.groupId,
      fulfilment: resolvedFulfilment,
      addOnCount: product.addOns.length,
      crossSellCount: product.crossSells.length,
    })

    // Unavailable extras (maxQuantity 0 for the current fulfilment)
    // are filtered out entirely so the user only sees cards they can
    // actually add. The `basket` memo below still re-validates before
    // submit as a defence against stale state.
    const nextAddOns: PurchaseModalExtra[] = product.addOns
      .sort((a, b) =>
        a.isMandatory === b.isMandatory ? 0 : a.isMandatory ? -1 : 1
      )
      .map(extra => {
        const maxQuantity = resolveExtraMax(extra, pickup, resolvedFulfilment)
        // Mandatory extras start at the parent line qty clamped to stock.
        const mandatoryBase = extra.isMandatory ? parentQuantity : 0
        const clampedMin = Math.min(mandatoryBase, maxQuantity)
        const desiredQty = extra.isMandatory ? parentQuantity : 0
        const quantity = Math.min(desiredQty, Math.max(0, maxQuantity))

        return {
          ...extra,
          name: extra.title,
          maxQuantity,
          minQuantity: clampedMin,
          quantity,
          parentGroupId: product.groupId,
          category: category ?? '',
        }
      })
      .filter(extra => extra.maxQuantity > 0)

    const nextCrossSells: PurchaseModalExtra[] = product.crossSells
      .filter(extra => extra.delivery.available && extra.delivery.maxQuantity > 0)
      .map(extra => ({
        ...extra,
        name: extra.title,
        quantity: 0,
        minQuantity: 0,
        // Cross-sells use the extra's own delivery cap (they're
        // always delivery-routed regardless of the parent's choice).
        maxQuantity: extra.delivery.maxQuantity,
        parentGroupId: product.groupId,
        category: category ?? '',
      }))

    setAddOns(nextAddOns)
    setCrossSells(nextCrossSells)

    debugRef.current('[usePurchaseModal] extras built', {
      addOns: nextAddOns.length,
      crossSells: nextCrossSells.length,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, charges, category, fulfilment, productType, parentQuantity])

  // ----------------------------
  // Stages — hire-and-extras flow uses 2 stages, everything else 1.
  // ----------------------------
  const hasAddons = addOns.length > 0
  const hasCross = crossSells.length > 0

  const totalStages = useMemo(
    () => (hasAddons && hasCross ? 2 : 1),
    [hasAddons, hasCross]
  )

  // Keep stage valid if availability changes (e.g. addOns disappear).
  useEffect(() => {
    if (!open) return

    const nextStage = Math.min(Math.max(1, currentStage), totalStages)
    if (nextStage !== currentStage) {
      debugRef.current('[usePurchaseModal] clamping stage', {
        from: currentStage,
        to: nextStage,
        totalStages,
      })
      setCurrentStage(nextStage)
    }
  }, [open, currentStage, totalStages])

  // ----------------------------
  // Extras basket derived from selections (no sync state).
  //
  // This is "what the user wants" — mandatory add-ons always belong,
  // optional ones belong once the user picks a non-zero qty. Bounds
  // enforcement and the defence-in-depth tamper check live below in
  // the `basket` memo (payload build) and in `quantityError` (submit
  // gate), so nothing bad reaches Lunar even if the stepper UI is
  // bypassed via dev tools.
  // ----------------------------
  const extrasBasket = useMemo(() => {
    return [
      ...addOns.filter(extra => extra.isMandatory || extra.quantity !== 0),
      ...crossSells.filter(extra => extra.quantity !== 0),
    ]
  }, [addOns, crossSells])

  // ----------------------------
  // Lunar basket payload derived from extrasBasket.
  //
  // Defence-in-depth: re-check each entry's bounds before building
  // the POST payload. If the user tampered with the disabled stepper
  // on an unavailable extra and set a non-zero quantity, this filter
  // strips it so the POST body never carries an extra the server
  // hasn't approved. `quantityError` already prevents the confirm
  // button from firing in those cases, but this is the last line.
  // ----------------------------
  const basket = useMemo<LunarProductPreBasket[]>(() => {
    if (extrasBasket.length === 0) return []

    // Cross-sells need the parent hire window so they can land on the
    // same dates as the main line. Pull these from the charges request.
    const parentStartDate = charges?.request.resolvedStartDate ?? ''
    const parentEndDate = charges?.request.resolvedEndDate ?? ''

    const payable = extrasBasket.filter(extra => {
      const max = Number(extra.maxQuantity ?? 0)
      const qty = Number(extra.quantity ?? 0)
      return (
        Number.isFinite(max) &&
        Number.isFinite(qty) &&
        max > 0 &&
        qty > 0 &&
        qty <= max
      )
    })

    return payable.map(extra => {
      // Mandatory add-ons: Brenda returns values already scaled to
      // the parent's quantity, so we use them directly.
      // Non-mandatory add-ons & cross-sells: Brenda returns per-unit
      // values, so we multiply by the item's own quantity.
      const qtyMultiplier = extra.isMandatory ? 1 : extra.quantity

      const item: LunarProductPreBasket = {
        stockCode: extra.stockCode,
        quantity: extra.quantity,
        price: toFixedNumber(extra.price.amount * qtyMultiplier, 2),
        productType: extra.productType,
        category: extra.category,
        title: extra.title,
        maxQuantity: extra.maxQuantity,
      }

      const isAddOn = addOns.some(a => a.stockCode === extra.stockCode)

      // Deposit — hire (bulk) linked items can carry their own deposit.
      if (extra.productType === 'bulk' && extra.deposit?.amount) {
        item.hireDeposit = toFixedNumber(
          extra.deposit.amount * qtyMultiplier, 2
        )
      }

      if (isAddOn) {
        if (extra.parentGroupId) {
          item.lineParentGroupId = extra.parentGroupId
        }
        if (extra.isMandatory) item.isMandatory = 1
        if (extra.productType === 'bulk' && extra.damageWaiver) {
          item.hireDamageWaiver = toFixedNumber(
            extra.damageWaiver * qtyMultiplier, 2
          )
        }
      } else {
        // Cross-sell (always non-mandatory)
        if (extra.delivery.date) item.deliveryDate = extra.delivery.date
        item.deliveryPrice =
          toFixedNumber((extra.delivery.price?.amount ?? 0) * qtyMultiplier, 2)
        item.fulfilment = 'delivery'

        if (extra.productType === 'bulk') {
          if (extra.damageWaiver) {
            item.hireDamageWaiver = toFixedNumber(
              extra.damageWaiver * qtyMultiplier, 2
            )
          }
          if (parentStartDate) item.hireStartDate = parentStartDate
          if (parentEndDate) item.hireEndDate = parentEndDate
        }
      }

      return item
    })
  }, [extrasBasket, addOns, charges])

  // ----------------------------
  // Quantity rules
  //
  // Gates the Confirm button. Errors for:
  //   - A mandatory add-on that can't be fulfilled (`maxQuantity <= 0`)
  //     — the parent can't ship without it, so submit must block.
  //   - Any entry whose quantity is outside `[minQuantity, maxQuantity]`.
  //   - Any entry with `max === 0` carrying a non-zero qty (tampering).
  // ----------------------------
  const quantityError = useMemo(() => {
    return extrasBasket.some(extra => {
      const qty = Number(extra.quantity ?? 0)
      const min = Number(extra.minQuantity ?? 0)
      const max = Number(extra.maxQuantity ?? 0)

      if (extra.isMandatory && max <= 0) return true
      if (max <= 0) return qty !== 0
      if (qty < min) return true
      if (qty > max) return true

      return false
    })
  }, [extrasBasket])

  // ----------------------------
  // Summary totals
  //
  // `grossTotal` (inc-VAT) is the Subtotal row. `vatTotal` is the sum
  // of each extra's VAT portion (preserved on `BrendaChargesExtra.vat`
  // by `mapBrendaExtra`) scaled by the same qty multiplier the basket
  // payload uses. `itemsExVat` is the inc-VAT total minus VAT — the
  // "Items for purchase" line in the Figma summary panel.
  // ----------------------------
  const grossTotal = useMemo(() => {
    if (basket.length === 0) return 0
    return basket.reduce((sum, item) => sum + item.price, 0)
  }, [basket])

  const vatTotal = useMemo(() => {
    if (extrasBasket.length === 0) return 0
    return extrasBasket.reduce((sum, extra) => {
      const max = Number(extra.maxQuantity ?? 0)
      const qty = Number(extra.quantity ?? 0)
      if (!(max > 0 && qty > 0 && qty <= max)) return sum
      const qtyMultiplier = extra.isMandatory ? 1 : qty
      const vatAmount = Number(extra.vat?.amount ?? 0)
      return sum + toFixedNumber(vatAmount * qtyMultiplier, 2)
    }, 0)
  }, [extrasBasket])

  // Ex-VAT split by product type. The summary panel renders these as
  // separate "Items for hire:" and "Items for purchase:" rows so the
  // user can see what they're paying for under each fulfilment
  // category before the VAT + Subtotal lines. Both use the same
  // qty-multiplier rule as the basket payload (mandatory = 1, others
  // multiply by the selected qty) so the breakdown reconciles with
  // grossTotal and vatTotal to the penny.
  const { hireExVat, purchaseExVat } = useMemo(() => {
    if (extrasBasket.length === 0) {
      return { hireExVat: 0, purchaseExVat: 0 }
    }
    return extrasBasket.reduce(
      (acc, extra) => {
        const max = Number(extra.maxQuantity ?? 0)
        const qty = Number(extra.quantity ?? 0)
        if (!(max > 0 && qty > 0 && qty <= max)) return acc
        const qtyMultiplier = extra.isMandatory ? 1 : qty
        const priceAmount = Number(extra.price?.amount ?? 0)
        const vatAmount = Number(extra.vat?.amount ?? 0)
        const exVat = toFixedNumber(
          (priceAmount - vatAmount) * qtyMultiplier,
          2
        )
        if (extra.productType === 'bulk') {
          acc.hireExVat = toFixedNumber(acc.hireExVat + exVat, 2)
        } else if (extra.productType === 'consumable') {
          acc.purchaseExVat = toFixedNumber(acc.purchaseExVat + exVat, 2)
        }
        return acc
      },
      { hireExVat: 0, purchaseExVat: 0 }
    )
  }, [extrasBasket])

  // ----------------------------
  // UI copy
  // ----------------------------
  // Mobile uses the new Figma copy (title-only). Desktop keeps the
  // pre-change title + description sub-line — exposed side-by-side so
  // the modal can render whichever block the breakpoint asks for.
  const header = useMemo(() => {
    const isAddOnStage = currentStage === 1 && addOns.length > 0
    return {
      mobileTitle: isAddOnStage
        ? 'Recommended Extras'
        : 'Our top picks to complete your project',
      title: isAddOnStage ? 'Extras for this item' : 'HSS recommends',
      description: isAddOnStage
        ? 'See a selection of recommended extras for your item.'
        : 'Our top picks to help you complete your project.',
    }
  }, [currentStage, addOns.length])

  // ----------------------------
  // Close handler
  // ----------------------------
  const handleClose = useCallback(() => {
    debugRef.current('[usePurchaseModal] handleClose')
    setCurrentStage(1)
    onClose()
  }, [onClose])

  // ----------------------------
  // Tracking
  // ----------------------------
  useEffect(() => {
    if (!open) return
    if (!hasAddons && !hasCross) return

    const addonType = 'Product Addons'
    const crossType = 'Product Cross Sells'

    const extrasType =
      totalStages === 1 && hasAddons
        ? addonType
        : totalStages === 1 && hasCross
          ? crossType
          : currentStage === 1
            ? addonType
            : crossType

    debugRef.current('[usePurchaseModal] trackPageInfo', {
      extrasType,
      productName,
      categoryType: productType === 'bulk' ? 'Hire' : 'Buy',
      loggedIn,
      currentStage,
      totalStages,
    })

    trackPageInfo({
      pageType: extrasType,
      pageTitle: productName,
      categoryType: productType === 'bulk' ? 'Hire' : 'Buy',
      loggedIn,
    })
  }, [
    open,
    totalStages,
    currentStage,
    hasAddons,
    hasCross,
    productName,
    productType,
    loggedIn,
  ])

  return {
    // Stages
    totalStages,
    currentStage,
    setCurrentStage,

    // Extras data
    addOns,
    crossSells,
    setAddOns,
    setCrossSells,

    // Derived outputs
    extrasBasket,
    basket,
    grossTotal,
    vatTotal,
    hireExVat,
    purchaseExVat,
    quantityError,

    // UI helpers
    header,

    // Handlers
    handleClose,
  }
}

export default usePurchaseModal
